import { inject, Injectable } from '@angular/core';
import { EjournalApiService } from '@et/api';
import { NotificationService } from '@et/notifications';
import { EtJournalSigner } from '@et/typings';
import { Store } from '@ngxs/store';
import { catchError, of, switchMap, tap } from 'rxjs';
import { UpdateSigners } from '../state/ejournal-session.actions';
import { EjournalSessionSelectors } from '../state/ejournal-session.selectors';
import { EjournalEntriesService } from './ejournal-entries.service';
import { EjournalSessionCancelService } from './ejournal-session-cancel.service';

@Injectable()
export class EjournalSignersService {
  private readonly ejournalApiService = inject(EjournalApiService);
  private readonly notificationService = inject(NotificationService);
  private readonly entriesService = inject(EjournalEntriesService);
  private readonly sessionCancel = inject(EjournalSessionCancelService);
  private readonly store = inject(Store);

  private readonly signers = this.store.selectSignal(
    EjournalSessionSelectors.getSigners,
  );

  /**
   * Deletes a signer from the eJournal and any entries linked via `ejournalEntryIds`.
   */
  deleteSigner(signerId: string) {
    const signer = this.signers().find((s) => s.eJournalSignerId === signerId);
    let entryIds: string[] = [];
    try {
      entryIds = JSON.parse(signer?.ejournalEntryIds || '[]');
      if (!Array.isArray(entryIds)) {
        entryIds = [];
      }
    } catch {
      entryIds = [];
    }

    const deleteEntries$ =
      entryIds.length > 0
        ? this.entriesService
            .unlinkEntriesFromSigners(entryIds)
            .pipe(
              switchMap(() =>
                this.ejournalApiService
                  .deleteEntries(entryIds)
                  .pipe(
                    tap(() =>
                      this.entriesService.removeEntriesFromLocalState(entryIds),
                    ),
                  ),
              ),
            )
        : of(null);

    return deleteEntries$.pipe(
      this.sessionCancel.takeUntilCancel(),
      switchMap(() => this.ejournalApiService.deleteSigner(signerId)),
      tap(() => {
        this.notificationService.showSuccess('Signer deleted successfully');
        this.updateSigners((signers) =>
          signers.filter((s) => s.eJournalSignerId !== signerId),
        );
      }),
      catchError((error) => {
        this.notificationService.showError('Failed to delete signer');
        throw error;
      }),
    );
  }

  /**
   * Deletes a signature for a signer and clears that signer's signatures in local state.
   */
  deleteSignerSignature(signerId: string, signatureId: string) {
    return this.ejournalApiService.deleteSignerSignature(signatureId).pipe(
      this.sessionCancel.takeUntilCancel(),
      tap(() => {
        this.notificationService.showSuccess('Signature deleted successfully');
        this.updateSigners((signers) =>
          signers.map((s) =>
            s.eJournalSignerId === signerId
              ? {
                  ...s,
                  signatures: s.signatures.filter(
                    (sig) => sig.id !== signatureId,
                  ),
                }
              : s,
          ),
        );
      }),
      catchError((error) => {
        this.notificationService.showError('Failed to delete signature');
        throw error;
      }),
    );
  }

  private updateSigners(
    updater: (signers: EtJournalSigner[]) => EtJournalSigner[],
  ) {
    this.store.dispatch(new UpdateSigners(updater));
  }
}
